import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { FaCertificate, FaArrowLeft, FaTimes, FaUpload } from "react-icons/fa";
import { useScrollLock } from "../../../hooks/useScrollLock";

import adminService from "../../../api/services/admin";
import { approveApplication } from "../../../store/slices/certificates/asyncThunks";
import { selectReviewLoading } from "../../../store/slices/certificates/slice";
import { axiosApiClient } from "../../../api";
import {
  SectionHeader,
  SectionTitle,
  BackButton,
  PageCard,
  PageCardBody,
  LoadingContainer,
  ErrorState,
} from "../shared/DetailPage.styles";
import {
  ApplicationDetails,
  ReviewForm,
  FormGroup,
  Label,
  SubmitButton,
  WorkImagesGrid,
  WorkImageItem,
  WorkImage,
  WorkImageName,
  SelectedFileInfo,
  ApprovedStatus,
  CertificateLink,
  FullscreenOverlay,
  FullscreenImage,
  FullscreenCloseButton,
} from "../../../components/CertificateReviewModal/CertificateReviewModal.styles";

const AdminCertificateReview = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const submittingReview = useSelector(selectReviewLoading);

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [certificatePdf, setCertificatePdf] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [workImageUrls, setWorkImageUrls] = useState([]);

  // Zaključaj skrol stranice dok je fullscreen pregled slike otvoren
  useScrollLock(Boolean(fullscreenImage));

  // workImages može stići kao JSON string — normalizuj u niz
  const normalizeApplication = (app) => {
    if (!app) return app;
    let workImages = app.workImages;
    if (typeof workImages === "string") {
      try {
        workImages = JSON.parse(workImages);
      } catch (e) {
        workImages = [];
      }
    }
    if (!Array.isArray(workImages)) workImages = [];
    return { ...app, workImages };
  };

  // Učitaj aplikaciju
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);

    adminService
      .getCertificateApplicationById(applicationId)
      .then((response) => {
        if (mounted) setApplication(normalizeApplication(response.application));
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [applicationId]);

  // Slike radova se serviraju kroz autentikovan API, pa se učitavaju kao blob
  useEffect(() => {
    if (!application?.id || !application?.workImages?.length) {
      setWorkImageUrls([]);
      return undefined;
    }

    let cancelled = false;
    let createdUrls = [];

    const loadImages = async () => {
      const urls = await Promise.all(
        application.workImages.map(async (_, index) => {
          try {
            const response = await axiosApiClient.get(
              `/certificates/work-image/${application.id}/${index}`,
              { responseType: "blob" },
            );
            return URL.createObjectURL(response.data);
          } catch (err) {
            console.error("Greška pri učitavanju slike rada:", err);
            return null;
          }
        }),
      );

      if (cancelled) {
        urls.forEach((url) => url && URL.revokeObjectURL(url));
        return;
      }

      createdUrls = urls;
      setWorkImageUrls(urls);
    };

    loadImages();

    return () => {
      cancelled = true;
      createdUrls.forEach((url) => url && URL.revokeObjectURL(url));
    };
  }, [application]);

  const goBack = () => navigate("/admin-panel/sertifikati");

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("sr-Latn-RS", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCertificateDownload = async (e) => {
    e.preventDefault();

    try {
      const response = await axiosApiClient.get(
        `/certificates/download/${application.id}`,
        { responseType: "blob" },
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sertifikat-${application.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("Greška pri preuzimanju sertifikata:", err);
      toast.error("Greška prilikom preuzimanja sertifikata");
    }
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Molimo odaberite PDF fajl");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("PDF fajl je prevelik (maksimalno 10MB)");
      return;
    }

    setCertificatePdf(file);
  };

  const handleApproveSubmit = async (e) => {
    e.preventDefault();

    if (!certificatePdf) {
      toast.error("Molimo okačite PDF sertifikat");
      return;
    }

    try {
      const result = await dispatch(
        approveApplication({
          applicationId: application.id,
          certificatePdf: certificatePdf,
        }),
      );

      if (result.type === "certificates/approveApplication/fulfilled") {
        toast.success(
          "Aplikacija je uspešno odobrena i sertifikat je poslat korisniku",
        );

        // Osveži lokalno stanje — admin odmah vidi odobren status
        setApplication((prev) => ({
          ...prev,
          status: "approved",
          certificateUrl: prev.certificateUrl || "approved",
          reviewedAt: new Date().toISOString(),
        }));
        setCertificatePdf(null);
      } else {
        throw new Error(result.error?.message || "Approval failed");
      }
    } catch (err) {
      console.error("Approve error:", err);
      toast.error("Greška prilikom odobravanja aplikacije");
    }
  };

  if (loading) {
    return (
      <LoadingContainer>
        <l-line-spinner size="40" stroke="3" speed="1" color="var(--accent)" />
      </LoadingContainer>
    );
  }

  if (error || !application) {
    return (
      <ErrorState>
        <h3>Aplikacija nije pronađena</h3>
        <p>Aplikacija ne postoji ili je u međuvremenu obrisana.</p>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad na sertifikate
        </BackButton>
      </ErrorState>
    );
  }

  return (
    <>
      <SectionHeader>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad
        </BackButton>
        <SectionTitle>
          <FaCertificate />
          Pregled Aplikacije za Sertifikat
        </SectionTitle>
      </SectionHeader>

      <PageCard>
        <PageCardBody>
          <ApplicationDetails>
            <div>
              <h4>Informacije o korisniku:</h4>
              <p>
                <strong>Ime:</strong> {application.user.firstName}{" "}
                {application.user.lastName}
              </p>
              <p>
                <strong>Email:</strong> {application.user.email}
              </p>
              <p>
                <strong>Telefon:</strong> {application.user.phone || "N/A"}
              </p>
            </div>

            <div>
              <h4>Informacije o kursu:</h4>
              <p>
                <strong>Kurs:</strong> {application.course.title}
              </p>
              <p>
                <strong>Datum aplikacije:</strong>{" "}
                {formatDate(application.appliedAt)}
              </p>
            </div>

            {application.description && (
              <div>
                <h4>Opis rada:</h4>
                <p>{application.description}</p>
              </div>
            )}

            <div>
              <h4>Slike rada:</h4>
              <WorkImagesGrid>
                {application.workImages &&
                  application.workImages.map((image, index) => (
                    <WorkImageItem
                      key={index}
                      onClick={() =>
                        workImageUrls[index] &&
                        setFullscreenImage(workImageUrls[index])
                      }
                    >
                      <WorkImage
                        src={workImageUrls[index] || undefined}
                        alt={`Rad ${index + 1}`}
                      />
                      <WorkImageName>{image.originalName}</WorkImageName>
                    </WorkImageItem>
                  ))}
              </WorkImagesGrid>
            </div>

            {application.status === "pending" ? (
              <ReviewForm onSubmit={handleApproveSubmit}>
                <FormGroup>
                  <Label>
                    <FaUpload />
                    Okačite PDF sertifikat za korisnika:
                  </Label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handlePdfChange}
                    required
                  />
                  {certificatePdf && (
                    <SelectedFileInfo>
                      Odabran fajl: {certificatePdf.name}
                    </SelectedFileInfo>
                  )}
                </FormGroup>

                <SubmitButton type="submit" disabled={submittingReview}>
                  {submittingReview
                    ? "Odobravamo..."
                    : "Odobri i pošalji sertifikat"}
                </SubmitButton>
              </ReviewForm>
            ) : (
              <ApprovedStatus>
                <p>✓ Aplikacija je već odobrena</p>
                {application.certificateUrl && (
                  <CertificateLink
                    href="#"
                    onClick={handleCertificateDownload}
                  >
                    <FaCertificate />
                    Preuzmi sertifikat
                  </CertificateLink>
                )}
              </ApprovedStatus>
            )}
          </ApplicationDetails>
        </PageCardBody>
      </PageCard>

      {fullscreenImage &&
        createPortal(
          <FullscreenOverlay onClick={() => setFullscreenImage(null)}>
            <FullscreenCloseButton onClick={() => setFullscreenImage(null)}>
              <FaTimes />
            </FullscreenCloseButton>
            <FullscreenImage
              src={fullscreenImage}
              alt="Fullscreen pregled"
              onClick={(e) => e.stopPropagation()}
            />
          </FullscreenOverlay>,
          document.body,
        )}
    </>
  );
};

export default AdminCertificateReview;
